import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { MapPinIcon } from '../utils/IconWrapper';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1558383817-8fbd5164d689?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const AttractionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const AttractionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryImage = styled.div`
  height: 300px;
  overflow: hidden;
  border-radius: 8px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const AttractionDetails: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Attrazioni | J.O.H.' 
            : 'Attractions | J.O.H.'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri le meravigliose attrazioni e luoghi di interesse vicino alle nostre proprietà in Toscana e Liguria.' 
            : 'Discover the wonderful attractions and places of interest near our properties in Tuscany and Liguria.'} 
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Attrazioni' : 'Attractions'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Esplora luoghi incantevoli e scopri esperienze uniche vicino alle nostre proprietà.'
              : 'Explore enchanting places and discover unique experiences near our properties.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Pianifica la Tua Visita' : 'Plan Your Visit'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <AttractionTitle>
            {language === 'it' ? 'Scopri la Bellezza della Toscana' : 'Discover the Beauty of Tuscany'}
          </AttractionTitle>
          
          <AttractionDescription>
            {language === 'it'
              ? 'La Toscana è una regione ricca di storia, cultura e paesaggi mozzafiato. Dalle famose città d\'arte come Firenze e Siena alle pittoresche colline del Chianti, ogni angolo offre esperienze indimenticabili.'
              : 'Tuscany is a region rich in history, culture, and breathtaking landscapes. From famous art cities like Florence and Siena to the picturesque Chianti hills, every corner offers unforgettable experiences.'}
          </AttractionDescription>
          
          <AttractionDescription>
            {language === 'it'
              ? 'Le nostre proprietà sono strategicamente posizionate per permetterti di esplorare facilmente le principali attrazioni della regione, immergendoti completamente nell\'autentico stile di vita toscano.'
              : 'Our properties are strategically positioned to allow you to easily explore the main attractions of the region, fully immersing yourself in the authentic Tuscan lifestyle.'}
          </AttractionDescription>
          
          <ImageGallery>
            <GalleryImage>
              <img 
                src="https://images.unsplash.com/photo-1543429233-4a722339c2c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                alt={language === 'it' ? 'Piazza del Campo, Siena' : 'Piazza del Campo, Siena'} 
              />
            </GalleryImage>
            <GalleryImage>
              <img 
                src="https://images.unsplash.com/photo-1534880643379-5487d87958db?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                alt={language === 'it' ? 'Vigneti del Chianti' : 'Chianti Vineyards'} 
              />
            </GalleryImage>
          </ImageGallery>
          
          <FeatureList>
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Città d\'Arte' : 'Art Cities'}</h3>
                <p>
                  {language === 'it'
                    ? 'Visita le celebri città d\'arte toscane come Firenze, Siena, Pisa e Lucca, con i loro tesori artistici, musei di fama mondiale e architettura storica.'
                    : 'Visit the famous Tuscan art cities like Florence, Siena, Pisa, and Lucca, with their artistic treasures, world-renowned museums, and historic architecture.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Enogastronomia' : 'Food and Wine'}</h3>
                <p>
                  {language === 'it'
                    ? 'Scopri l\'eccellenza enogastronomica toscana con degustazioni di vini nelle cantine del Chianti, lezioni di cucina tradizionale e visite ai mercati locali.'
                    : 'Discover Tuscan food and wine excellence with wine tastings in Chianti cellars, traditional cooking classes, and visits to local markets.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Paesaggi Naturali' : 'Natural Landscapes'}</h3>
                <p>
                  {language === 'it'
                    ? 'Immergiti nei paesaggi idilliaci delle colline toscane, esplora borghi medievali e goditi panorami mozzafiato che hanno ispirato artisti per secoli.'
                    : 'Immerse yourself in the idyllic landscapes of the Tuscan hills, explore medieval villages, and enjoy breathtaking views that have inspired artists for centuries.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <AttractionTitle>
            {language === 'it' ? 'Il Fascino della Liguria' : 'The Charm of Liguria'}
          </AttractionTitle>
          
          <AttractionDescription>
            {language === 'it'
              ? 'La Liguria, con la sua costa spettacolare e i suoi pittoreschi borghi colorati, offre un\'esperienza unica tra mare e montagna. Le Cinque Terre, Portofino e Genova sono solo alcune delle meraviglie da scoprire in questa regione.'
              : 'Liguria, with its spectacular coastline and picturesque colorful villages, offers a unique experience between the sea and the mountains. The Cinque Terre, Portofino, and Genoa are just some of the wonders to discover in this region.'}
          </AttractionDescription>
          
          <ImageGallery>
            <GalleryImage>
              <img 
                src="https://images.unsplash.com/photo-1498131372944-c03ee04c8d0e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                alt={language === 'it' ? 'Cinque Terre' : 'Cinque Terre'} 
              />
            </GalleryImage>
            <GalleryImage>
              <img 
                src="https://images.unsplash.com/photo-1552188984-4feeda9d9a21?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
                alt={language === 'it' ? 'Portofino' : 'Portofino'} 
              />
            </GalleryImage>
          </ImageGallery>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Pronto a Esplorare?' : 'Ready to Explore?'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci per pianificare la tua visita e scoprire itinerari personalizzati in base ai tuoi interessi e al tempo a disposizione.'
                : 'Contact us to plan your visit and discover personalized itineraries based on your interests and available time.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default AttractionDetails; 